import { orgAPI } from "@/api/api";
import { useUpdateCanvas } from "@/api/canvas";
import {
  useCopyFileRecord,
  useDeleteFileRecord,
  useMoveFileRecord,
} from "@/api/fileRecords";
import { useWorkspaces } from "@/api/workspaces";
import {
  useFileConflictResolver,
  type IntendedMove,
} from "@/components/FileConflictResolver";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { UnifiedItem } from "@/pages/workspaces/types";
import { moveRecordCascading } from "@/pages/workspaces/utils/folderMove";
import { cn } from "@/utils";
import type { Canvas, FileRecord } from "@cohesive/api-client";
import { Folder, Layers, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FolderTreeItem, buildFolderTree } from "./folderTree";

export interface BulkMoveDialogProps {
  onClose: () => void;
  workspaceId: string;
  allRecords: FileRecord[];
  items: UnifiedItem[];
  onSuccess?: () => void;
}

function leafName(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segments = cleaned.split("/").filter(Boolean);
  return segments[segments.length - 1] || path;
}

/** Paths we must exclude from the destination tree: the folders being moved
 *  themselves (can't move a folder into itself) and their descendants. */
function buildForbiddenPaths(items: UnifiedItem[]): Set<string> {
  const forbidden = new Set<string>();
  for (const item of items) {
    if (item.kind === "folder") {
      forbidden.add((item.data as FileRecord).path);
    }
  }
  return forbidden;
}

/**
 * Bulk-move dialog.
 *
 * Two modes depending on selection composition:
 * - Canvases only: pick a destination *workspace*.
 * - Anything with files/folders: pick a destination folder within the
 *   current workspace. Any canvases in the mixed selection are intentionally
 *   excluded from this operation (flagged with an inline note) because
 *   canvases live at workspace root and have no folder concept.
 */
export function BulkMoveDialog({
  onClose,
  workspaceId,
  allRecords,
  items,
  onSuccess,
}: BulkMoveDialogProps) {
  const fileOrFolderItems = useMemo(
    () => items.filter((i) => i.kind === "file" || i.kind === "folder"),
    [items],
  );
  const canvasItems = useMemo(
    () => items.filter((i) => i.kind === "canvas"),
    [items],
  );
  const mode: "canvases" | "records" =
    fileOrFolderItems.length === 0 && canvasItems.length > 0
      ? "canvases"
      : "records";

  const folderTree = useMemo(() => buildFolderTree(allRecords), [allRecords]);
  const forbidden = useMemo(
    () => buildForbiddenPaths(fileOrFolderItems),
    [fileOrFolderItems],
  );

  const [selectedPath, setSelectedPath] = useState("");
  const [targetWorkspaceId, setTargetWorkspaceId] = useState("");

  useEffect(() => {
    setSelectedPath("");
    setTargetWorkspaceId("");
  }, []);

  const { data: workspaces = [] } = useWorkspaces();
  const eligibleWorkspaces = useMemo(() => {
    const sorted = [...workspaces].sort((a, b) => {
      if (a.is_private === b.is_private) return a.name.localeCompare(b.name);
      return a.is_private ? 1 : -1;
    });
    return sorted.filter((ws) => ws.id !== workspaceId);
  }, [workspaces, workspaceId]);

  const moveFileRecord = useMoveFileRecord(workspaceId);
  const copyFileRecord = useCopyFileRecord(workspaceId);
  const deleteFileRecord = useDeleteFileRecord(workspaceId);
  const updateCanvas = useUpdateCanvas();
  const { resolveMoves } = useFileConflictResolver();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitRecords = async () => {
    if (fileOrFolderItems.length === 0) return;
    // Disallow moving an item to the folder it's already in. The API would
    // still succeed but it's confusing to the user.
    const moves: IntendedMove[] = fileOrFolderItems.map((item) => {
      const record = item.data as FileRecord;
      const isFolder = item.kind === "folder";
      const cleaned = record.path.endsWith("/")
        ? record.path.slice(0, -1)
        : record.path;
      const sourceName = leafName(cleaned);
      return {
        fileRecordId: record.id,
        sourceName,
        destinationParentPath: selectedPath,
        isFolder,
      };
    });

    setIsSubmitting(true);
    try {
      const ops = await resolveMoves({
        moves,
        sourceRecords: allRecords,
        destinationWorkspaceId: workspaceId,
      });
      if (ops === null) return;
      if (ops.length === 0) {
        onClose();
        return;
      }
      for (const op of ops) {
        switch (op.kind) {
          case "move": {
            // Folder moves cascade so descendants travel with the folder
            // marker; otherwise the destination ends up with an empty
            // folder and orphaned children at the source.
            const sourceRecord = allRecords.find(
              (r) => r.id === op.fileRecordId,
            );
            if (!sourceRecord) {
              await moveFileRecord.mutateAsync({
                fileRecordId: op.fileRecordId,
                payload: op.payload,
              });
              break;
            }
            await moveRecordCascading({
              record: sourceRecord,
              payload: op.payload,
              allRecords,
              moveOne: (vars) => moveFileRecord.mutateAsync(vars),
            });
            break;
          }
          case "replace_contents":
            await copyFileRecord.mutateAsync({
              fileRecordId: op.sourceFileRecordId,
              payload: op.payload,
            });
            break;
          case "delete":
            await deleteFileRecord.mutateAsync(op.fileRecordId);
            break;
        }
      }
      toast.success(
        moves.length === 1
          ? "Moved successfully"
          : `Moved ${moves.length} items`,
      );
      onSuccess?.();
      onClose();
    } catch {
      toast.error("Failed to move");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitCanvases = async () => {
    if (canvasItems.length === 0 || !targetWorkspaceId) return;
    setIsSubmitting(true);
    let moved = 0;
    let failed = 0;
    try {
      for (const item of canvasItems) {
        const canvas = item.data as Canvas;
        // Re-fetch latest to avoid overwriting stale fields, matching
        // the MoveCanvasDialog pattern. A fetch failure (e.g. canvas
        // deleted concurrently) counts as a failed move so we can
        // surface it in the toast rather than silently skipping.
        let latest: Canvas;
        try {
          latest = await orgAPI.canvas.getById(canvas.id);
        } catch {
          failed += 1;
          continue;
        }
        try {
          await updateCanvas.mutateAsync({
            id: canvas.id,
            data: {
              name: latest.name,
              description: latest.description,
              metadata: latest.metadata,
              workspace_id: targetWorkspaceId,
            },
          });
          moved += 1;
        } catch {
          failed += 1;
        }
      }

      // Only claim success if at least one canvas actually moved.
      // Silent "Moved N canvases" toasts after all fetches failed
      // (e.g. concurrent deletes) were misleading users into thinking
      // the operation worked.
      if (moved === 0) {
        toast.error(
          canvasItems.length === 1 ? "Failed to move" : "Failed to move",
        );
        return;
      }

      if (failed === 0) {
        toast.success(moved === 1 ? "Canvas moved" : `Moved ${moved} canvases`);
      } else {
        toast.success(
          `Moved ${moved} of ${canvasItems.length} canvases, ${failed} failed`,
        );
      }
      onSuccess?.();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = mode === "canvases" ? !!targetWorkspaceId : !isSubmitting;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "canvases"
              ? canvasItems.length === 1
                ? "Move canvas"
                : `Move ${canvasItems.length} canvases`
              : fileOrFolderItems.length === 1
                ? "Move to"
                : `Move ${fileOrFolderItems.length} items`}
          </DialogTitle>
          <DialogDescription>
            {mode === "canvases"
              ? "Choose a workspace."
              : "Choose a destination folder."}
          </DialogDescription>
        </DialogHeader>

        {mode === "canvases" ? (
          <div className="py-2">
            <Select
              value={targetWorkspaceId}
              onValueChange={setTargetWorkspaceId}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Select a workspace…" />
              </SelectTrigger>
              <SelectContent>
                {eligibleWorkspaces.length === 0 ? (
                  <div className="px-3 py-6 text-sm text-muted-foreground text-center">
                    No other workspaces available.
                  </div>
                ) : (
                  eligibleWorkspaces.map((ws) => {
                    const Icon = ws.is_private ? User : Layers;
                    const label = ws.is_private ? "My Workspace" : ws.name;
                    return (
                      <SelectItem key={ws.id} value={ws.id} className="py-2.5">
                        <span className="flex items-center gap-2.5">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-foreground/[0.06] dark:bg-n-95/[0.06]">
                            <Icon className="h-3.5 w-3.5 text-foreground/60" />
                          </span>
                          <span className="text-[13px] font-medium truncate">
                            {label}
                          </span>
                        </span>
                      </SelectItem>
                    );
                  })
                )}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <>
            <div className="py-2 max-h-64 overflow-y-auto">
              <button
                type="button"
                onClick={() => setSelectedPath("")}
                className={cn(
                  "w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors",
                  selectedPath === ""
                    ? "bg-foreground/10 text-foreground font-medium"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Folder className="h-4 w-4 shrink-0" />
                <span>Workspace root</span>
              </button>
              {folderTree.map((node) => (
                <FolderTreeItem
                  key={node.path}
                  node={node}
                  selected={selectedPath}
                  onSelect={setSelectedPath}
                  forbidden={forbidden}
                />
              ))}
              {folderTree.length === 0 && (
                <p className="text-xs text-muted-foreground px-2 py-3">
                  No folders in this workspace yet.
                </p>
              )}
            </div>
            {canvasItems.length > 0 && (
              <p className="text-xs text-muted-foreground px-1 -mt-1">
                {canvasItems.length === 1
                  ? "1 canvas in your selection will stay in this workspace; canvases can't be moved into folders."
                  : `${canvasItems.length} canvases in your selection will stay in this workspace; canvases can't be moved into folders.`}
              </p>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={
              mode === "canvases" ? handleSubmitCanvases : handleSubmitRecords
            }
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? "Moving…" : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
